import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { ROUTES, CONTACT_INFO } from '../../utils/constants';
import { OpenAccountButton } from '../ui/OpenAccountButton';
import styles from './Header.module.css';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  const navLinks = [
    { path: ROUTES.HOME, label: t('nav.home') },
  ];

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <Link 
          to={ROUTES.HOME} 
          className={styles.logo}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          GC BUSINESS
        </Link>

        {/* Desktop Navigation */}
        <ul className={styles.navLinks}>
          {navLinks.map((link) => (
            <li key={link.path}>
              {link.external ? (
                <a
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.navLink}
                  aria-label={`${link.label} (opens in new window)`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.path}
                  className={location.pathname === link.path ? styles.active : ''}
                  onClick={link.path === ROUTES.HOME ? () => window.scrollTo({ top: 0, behavior: 'smooth' }) : undefined}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
          <li>
            <a
              href={CONTACT_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.whatsappLink}
              aria-label={`${t('nav.whatsapp')} (opens in new window)`}
            >
              {t('nav.whatsapp')}
            </a>
          </li>
          <li>
            <a
              href="https://pay.gcbusiness.space/site/home"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.loginLink}
              aria-label="Login (opens in new window)"
            >
              Login
            </a>
          </li>
        </ul>

        {/* CTA Button */}
        <OpenAccountButton 
          className={styles.ctaButton}
          size="medium"
        />

        {/* Mobile Hamburger */}
        <button
          className={styles.hamburger}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className={styles.mobileMenu}>
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className={styles.mobileNavLink}
                aria-label={`${link.label} (opens in new window)`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => {
                  setIsMenuOpen(false);
                  if (link.path === ROUTES.HOME) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className={location.pathname === link.path ? styles.active : ''}
              >
                {link.label}
              </Link>
            )
          ))}
          
          <a
            href={CONTACT_INFO.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMenuOpen(false)}
            className={styles.mobileWhatsappLink}
            aria-label={`${t('nav.whatsapp')} (opens in new window)`}
          >
            {t('nav.whatsapp')}
          </a>
          
          <a
            href="https://pay.gcbusiness.space/site/home"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMenuOpen(false)}
            className={styles.mobileLoginLink}
            aria-label="Login (opens in new window)"
          >
            Login
          </a>
          
          <OpenAccountButton
            onClick={() => setIsMenuOpen(false)}
            className={styles.mobileCtaButton}
            size="medium"
          />
        </div>
      )}
    </header>
  );
};
